/**
 * NIR PBX — Main app bootstrap.
 * Pure Vanilla JS, no frameworks.
 */

import { api } from './api';
import { auth, isAuthenticated, login } from './auth';
import { router } from './router';
import { renderSidebar } from './components/sidebar';
import { renderHeader } from './components/header';
import { presencePoller } from './services/presence-poller';
import { initSentry } from './sentry';
import { $user, $isAuthenticated } from './stores/auth';
import type { AppUser } from './types/auth';

declare global {
    interface Window {
        __pbxUser: AppUser;
    }
}

async function init(): Promise<void> {
    // -----------------------------------------------------------
    // 1. Check Keycloak OIDC authentication
    // -----------------------------------------------------------
    let user: AppUser | null;
    try {
        user = await auth.init();
    } catch (e: any) {
        console.warn('Auth init failed:', (e instanceof Error ? e.message : String(e)));
        user = null;
    }

    // If not authenticated, redirect to Keycloak login.
    // On localhost dev, allow fallback to mock/DEV_MODE.
    if (!user && !isAuthenticated()) {
        const host = window.location.hostname;
        if (host !== 'localhost' && host !== '127.0.0.1') {
            // Production — must authenticate via Keycloak
            console.warn('Not authenticated, redirecting to Keycloak');
            await login();
            return; // Stop init — page will redirect
        }
        console.warn('Not authenticated but running on localhost — continuing with DEV_MODE fallback');
    }

    // -----------------------------------------------------------
    // 2. Initialize API client
    // -----------------------------------------------------------
    try {
        await api.init();
    } catch (e: any) {
        console.warn('API init failed, using mock data:', (e instanceof Error ? e.message : String(e)));
    }

    // -----------------------------------------------------------
    // 3. Enrich user profile from API (extension, department, etc.)
    // -----------------------------------------------------------
    if (!user || !user.extension) {
        try {
            const profile = await api.get('/system/me');
            user = {
                name: profile.full_name || user?.name || 'User',
                username: profile.username || user?.username || '',
                email: profile.email || user?.email || '',
                extension: profile.extension || '',
                role: profile.role || 'user',
                department_name: profile.department_name || '',
                id: profile.id || user?.sub || '',
                roles: user?.roles || [],
                schedule_type: profile.schedule_type || 'office_hours',
                schedule_id: profile.schedule_id || '',
                schedule_name: profile.schedule_name || '',
            };
        } catch (e: any) {
            console.warn('Failed to fetch profile from /api/system/me:', (e instanceof Error ? e.message : String(e)));
            // Last resort fallback — show empty until auth is configured
            if (!user) {
                user = { name: 'User', username: '', email: '', extension: '', role: 'user', roles: [] };
            }
        }
    }

    // Store user in nanostore + legacy global
    $user.set(user);
    $isAuthenticated.set(true);
    window.__pbxUser = user; // legacy — pages migrating to $user.get()
    if (auth.setUser) auth.setUser(user);

    // Load role-specific module (determines nav, routes, dashboard widgets)
    const roleName =
        user.role === 'admin' || user.roles?.includes('admin') || user.roles?.includes('pbx-admin')
            ? 'admin'
            : user.role === 'dept-head' || user.roles?.includes('dept-head')
              ? 'head'
              : 'user';
    try {
        const roleModule = await import(`./roles/${roleName}.ts`);
        roleModule.init(user);
    } catch (e: any) {
        console.warn(`Failed to load role module "${roleName}", falling back to user:`, (e instanceof Error ? e.message : String(e)));
        const fallback = await import('./roles/user');
        fallback.init(user);
    }

    // Initialize error tracking (Sentry / GlitchTip)
    initSentry(user);

    // Render layout components
    renderSidebar(document.getElementById('sidebar')!, user);
    renderHeader(document.getElementById('header')!, user);

    // Initialize WebRTC softphone
    try {
        const { initSoftphone } = await import('../phone/softphone-ui');
        const spPanel = document.getElementById('softphone-panel')!;
        initSoftphone(spPanel, user);
    } catch (e: any) {
        console.warn('Softphone init failed:', (e instanceof Error ? e.message : String(e)));
    }

    // Start presence poller for current user
    if (user.extension) {
        presencePoller.track([user.extension]);
        presencePoller.start();
    }

    router.init();
}

init();
